/**
 * Sentry Configuration
 *
 * Initialize Sentry for error tracking with:
 * - PII scrubbing (no cookies, auth headers)
 * - requestId tagging for correlation
 * - Deny-list for noisy 4xx errors (CSRF, rate limits)
 * - Tunnel endpoint support (if configured)
 */

import * as Sentry from '@sentry/react';

// Error codes to ignore (keep in metrics, don't alert)
const IGNORED_ERROR_CODES = ['CSRF_INVALID', 'RATE_LIMITED', 'UNAUTHORIZED', 'FORBIDDEN'];

// Patterns to ignore (network errors, user aborts)
const IGNORED_ERROR_PATTERNS = [
  /AbortError/i,
  /NetworkError/i,
  /Failed to fetch/i,
  /Load failed/i,
  /cancelled/i,
  /user aborted/i,
];

/**
 * Detect errors fired by Vite HMR / React Fast Refresh during a hot-reload swap.
 *
 * When a developer saves a file mid-edit, Fast Refresh tries to re-render the
 * currently-mounted tree against the new module. If the new module no longer
 * exports a symbol that a captured closure still references, a
 * `ReferenceError: <symbol> is not defined` fires from inside React Refresh's
 * scheduler. These aren't real bugs — they're an artifact of the edit cycle —
 * but they were polluting Sentry with ~40 issues/week from local dev sessions.
 *
 * The stack signature is consistent:
 *   - frame at `/@react-refresh`
 *   - frame at React DevTools extension `installHook.js`
 *   - function `performReactRefresh` or `scheduleRefresh`
 *
 * Filtering on any of these gives us a sharp cut: real dev errors (manual
 * captureException, network errors, runtime bugs) still surface; HMR noise
 * is dropped at the source.
 */
function isHmrRefreshError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
  return frames.some((f) => {
    const filename = (f.filename ?? '').toLowerCase();
    const fn = (f.function ?? '').toLowerCase();
    return (
      filename.includes('/@react-refresh') ||
      filename.includes('installhook.js') ||
      fn === 'performreactrefresh' ||
      fn === 'schedulerefresh' ||
      fn.endsWith('.schedulerefresh')
    );
  });
}

/**
 * Check if an error should be ignored by Sentry
 */
function shouldIgnoreError(event: Sentry.ErrorEvent): boolean {
  // HMR / Fast Refresh artifacts — see isHmrRefreshError() comment.
  if (isHmrRefreshError(event)) {
    return true;
  }

  const message = event.message || event.exception?.values?.[0]?.value || '';

  // Check error codes in response data
  const errorCode = (event.extra as Record<string, unknown>)?.code as string;
  if (errorCode && IGNORED_ERROR_CODES.includes(errorCode)) {
    return true;
  }

  // Check patterns
  return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Scrub sensitive data from events
 */
function scrubSensitiveData(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  // Remove cookies
  if (event.request) {
    delete event.request.cookies;

    // Scrub Authorization headers
    if (event.request.headers) {
      const headers = event.request.headers as Record<string, string>;
      if (headers['Authorization']) {
        headers['Authorization'] = '[[REDACTED]]';
      }
      if (headers['authorization']) {
        headers['authorization'] = '[[REDACTED]]';
      }
      if (headers['Cookie']) {
        headers['Cookie'] = '[[REDACTED]]';
      }
      if (headers['cookie']) {
        headers['cookie'] = '[[REDACTED]]';
      }
    }
  }

  // Scrub request body if it looks like it contains sensitive data
  if (event.request?.data) {
    const data = event.request.data as Record<string, unknown>;
    const sensitiveFields = ['password', 'token', 'secret', 'apiKey', 'api_key'];
    for (const field of sensitiveFields) {
      if (data[field]) {
        data[field] = '[[REDACTED]]';
      }
    }
  }

  return event;
}

/**
 * Initialize Sentry with proper configuration
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    // OBS-14: in production a missing DSN silently kills all frontend error tracking,
    // so fail loud instead of an info line. (VITE_SENTRY_DSN is inlined at BUILD time —
    // if this fires in prod it usually means the var wasn't set on the *build* env.)
    if (import.meta.env.PROD) {
      console.error(
        '[Sentry] CRITICAL: VITE_SENTRY_DSN is not set in production — frontend error tracking is DISABLED. ' +
          'It must be a Cloudflare Pages *build* environment variable (Vite inlines it at build time).',
      );
    } else {
      console.info('[Sentry] No DSN configured, error tracking disabled');
    }
    return;
  }

  const environment = import.meta.env.VITE_ENVIRONMENT || (import.meta.env.PROD ? 'production' : 'development');

  const release = import.meta.env.VITE_GIT_SHA || import.meta.env.VITE_APP_VERSION || 'unknown';

  // Tunnel endpoint (optional, to avoid ad blockers)
  const tunnel = import.meta.env.VITE_SENTRY_TUNNEL;

  Sentry.init({
    dsn,
    environment,
    release,
    tunnel,

    // PII: we keep sendDefaultPii false and scrub in beforeSend (privacy-safe)
    sendDefaultPii: false,

    // Send console.warn/error as logs to Sentry (optional)
    enableLogs: true,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Only capture replays on errors
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Send console.warn and console.error as Sentry logs
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],

    // Sampling
    // 100% error sampling for launch week
    sampleRate: 1.0,
    // 5-10% tracing (adjust based on volume)
    tracesSampleRate: import.meta.env.PROD ? 0.05 : 1.0,
    // 10% replay on error
    replaysOnErrorSampleRate: 0.1,
    // 0% session replay (no general sessions)
    replaysSessionSampleRate: 0,

    // Filter and scrub
    beforeSend(event) {
      // Ignore noisy errors
      if (shouldIgnoreError(event)) {
        return null;
      }

      // Scrub sensitive data
      return scrubSensitiveData(event);
    },

    // Don't send certain things to breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Don't track console.debug
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }
      return breadcrumb;
    },
  });

  console.info(`[Sentry] Initialized: env=${environment}, release=${release}`);
}

/**
 * Set user context (call after authentication)
 */
export function setSentryUser(user: { id: string; coachId?: string }): void {
  // Only set hashed IDs, not actual values
  Sentry.setUser({
    id: hashId(user.id),
  });

  if (user.coachId) {
    Sentry.setTag('coachId_hash', hashId(user.coachId));
  }
}

/**
 * Clear user context (call on logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Set request ID context (call on API requests)
 */
export function setSentryRequestId(requestId: string): void {
  Sentry.setTag('requestId', requestId);
  Sentry.setContext('request', { requestId });
}

/**
 * Set endpoint context
 */
export function setSentryEndpoint(endpoint: string): void {
  Sentry.setTag('endpoint', endpoint);
}

/**
 * Capture an exception with extra context
 */
export function captureError(
  error: Error,
  context?: {
    requestId?: string;
    endpoint?: string;
    extra?: Record<string, unknown>;
  },
): void {
  if (context?.requestId) {
    setSentryRequestId(context.requestId);
  }
  if (context?.endpoint) {
    setSentryEndpoint(context.endpoint);
  }

  Sentry.captureException(error, {
    extra: context?.extra,
  });
}

const reportedSwallows = new Set<string>();

/**
 * Report an error a `catch` deliberately absorbs so the UI can degrade (OBS-11).
 * The frontend twin of the backend's `reportCaught`: the user carries on, but the failure
 * is no longer invisible. Sent as a `warning`, tagged `swallowed_at: <where>`.
 *
 * At most ONE event per `where` per page load: several callers sit in render paths or
 * per-row loops, and an unbounded report there would flood Sentry with one failure.
 * `where` must be a stable literal (e.g. 'ProgramBuilder.loadTemplate.workout'), never an
 * interpolated id, or the dedupe stops working.
 */
export function reportSwallowed(where: string, err: unknown, extra?: Record<string, unknown>): void {
  if (reportedSwallows.has(where)) return;
  reportedSwallows.add(where);
  Sentry.captureException(asError(err, `Swallowed error at ${where}`), {
    level: 'warning',
    tags: { swallowed_at: where },
    extra,
  });
}

/** Test seam: forget which sites have reported. */
export function __resetReportedSwallows(): void {
  reportedSwallows.clear();
}

/**
 * Hash an ID for privacy (simple hash, not cryptographic)
 */
function hashId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `h_${Math.abs(hash).toString(16)}`;
}

/**
 * Wrap a bare Supabase / Postgres error object in a real Error so Sentry can
 * capture a stack trace at the throw site. Supabase returns errors as plain
 * objects with `{ code, details, hint, message }` — when those are thrown or
 * rejected directly, V8 has no Error to attach a stack to and Sentry logs
 * "(no in-app frames)" with type "UnhandledRejection". Wrapping preserves all
 * original fields on the resulting Error so existing error-handling code that
 * inspects `.code` / `.details` keeps working.
 *
 * Idempotent: returns the input unchanged if it's already an Error.
 */
export function asError(
  err: unknown,
  fallbackMessage = 'Operation failed',
): Error & { code?: string; details?: string; hint?: string } {
  if (err instanceof Error) return err as Error;
  if (err && typeof err === 'object') {
    const obj = err as { code?: string; details?: string; hint?: string; message?: string };
    const wrapped: Error & { code?: string; details?: string; hint?: string } = new Error(
      obj.message ?? fallbackMessage,
    );
    if (obj.code !== undefined) wrapped.code = obj.code;
    if (obj.details !== undefined) wrapped.details = obj.details;
    if (obj.hint !== undefined) wrapped.hint = obj.hint;
    return wrapped;
  }
  return new Error(typeof err === 'string' ? err : fallbackMessage);
}

// Re-export Sentry for direct usage
export { Sentry };
